import { z } from "zod"
import { QuestionType } from "./checklist-form"

const checklistHeaderAnswerSpecSchema = z.object({
    type: z.literal("header"),
    questionId: z.string(),
})

const checklistTextAnswerSpecSchema = z.object({
    type: z.literal("text"),
    questionId: z.string(),
    answer: z.string(),
})

const checklistMultiSelectAnswerSpecSchema = z.object({
    type: z.literal("multi-select"),
    questionId: z.string(),
    answer: z.array(z.boolean()),
})

const checklistRadioAnswerSpecSchema = z.object({
    type: z.literal("radio"),
    questionId: z.string(),
    answer: z.array(z.boolean()),
})

const checklistUserSelectAnswerSpecSchema = z.object({
    type: z.literal("user-select"),
    questionId: z.string(),
    answer: z.string(),
})

const checklistAircraftSelectAnswerSpecSchema = z.object({
    type: z.literal("aircraft-select"),
    questionId: z.string(),
    answer: z.string(),
})

export const checklistAnswerSpecSchema = z.discriminatedUnion("type", [
    checklistHeaderAnswerSpecSchema,
    checklistTextAnswerSpecSchema,
    checklistMultiSelectAnswerSpecSchema,
    checklistRadioAnswerSpecSchema,
    checklistUserSelectAnswerSpecSchema,
    checklistAircraftSelectAnswerSpecSchema,
])

export const checklistAnswersSchema = z.record(z.string(), checklistAnswerSpecSchema)

export const checklistSubmissionRecordSchema = z.object({
    id: z.guid(),
    org_id: z.string(),
    checklist_id: z.string(),
    checklist_version: z.number(),
    mission_ids: z.array(z.string()).default([]),
    user_id: z.string().nullable(),
    created_at: z.string(),
    filled_at: z.string(),
    answers: checklistAnswersSchema,
})

export const checklistSubmissionSchema = checklistSubmissionRecordSchema.transform((r) => ({
    id: r.id,
    orgId: r.org_id,
    checklistId: r.checklist_id,
    checklistVersion: r.checklist_version,
    missionIds: r.mission_ids,
    userId: r.user_id,
    createdAt: r.created_at,
    filledAt: r.filled_at,
    answers: r.answers,
}))

export type ChecklistAnswerSpec<T extends QuestionType = QuestionType> = Extract<
    z.infer<typeof checklistAnswerSpecSchema>,
    { type: T }
>
export type ChecklistAnswers = z.infer<typeof checklistAnswersSchema>
export type ChecklistSubmissionRecord = z.infer<typeof checklistSubmissionRecordSchema>
export type ChecklistSubmission = z.infer<typeof checklistSubmissionSchema>

const submissionXorRefinement = (
    val: { missionIds: string[]; aircraftId: string | null; pilotInCommandId: string | null },
    ctx: z.RefinementCtx
) => {
    const hasMissions = val.missionIds.length > 0
    const hasMatchCriteria = val.aircraftId !== null && val.pilotInCommandId !== null

    if (hasMissions && hasMatchCriteria) {
        ctx.addIssue({
            code: "custom",
            message:
                "A submission must have either mission IDs or aircraft/pilot criteria, not both",
        })
    }
    if (!hasMissions && !hasMatchCriteria) {
        ctx.addIssue({
            code: "custom",
            message:
                "A submission must have either at least one mission ID or both aircraftId and pilotInCommandId",
        })
    }
}

const creatableChecklistSubmissionBaseSchema = z.object({
    orgId: z.string(),
    checklistId: z.string(),
    checklistVersion: z.number(),
    missionIds: z.array(z.string()),
    userId: z.guid().nullable(),
    answers: checklistAnswersSchema,
    aircraftId: z.guid().nullable(),
    pilotInCommandId: z.guid().nullable(),
    matchUntil: z.coerce.string().nullable(),
    id: z.guid().optional(),
    filledAt: z.iso.datetime().optional(),
})

export const creatableChecklistSubmissionSchema =
    creatableChecklistSubmissionBaseSchema.superRefine(submissionXorRefinement)
export type CreatableChecklistSubmission = z.infer<typeof creatableChecklistSubmissionSchema>

export const checklistSubmissionCursorSchema = z.object({
    filledAt: z.string(),
    id: z.guid(),
})
export type ChecklistSubmissionCursor = z.infer<typeof checklistSubmissionCursorSchema>

export const listChecklistSubmissionsResponseSchema = z.object({
    records: z.array(checklistSubmissionSchema),
    nextCursor: checklistSubmissionCursorSchema.nullable(),
})
export type ListChecklistSubmissionsResponse = z.infer<
    typeof listChecklistSubmissionsResponseSchema
>

export const missionChecklistSubmissionSchema = z.object({
    id: z.guid(),
    checklistId: z.string(),
    checklistName: z.string(),
    userId: z.guid().nullable(),
    createdAt: z.string(),
})
export type MissionChecklistSubmission = z.infer<typeof missionChecklistSubmissionSchema>

const retroactiveGuestSubmitBodySchema = z.object({
    answers: checklistAnswersSchema,
    checklistVersion: z.number(),
    missionIds: z.array(z.string()).min(1),
    id: z.guid().optional(),
    filledAt: z.iso.datetime().optional(),
})

const preemptiveGuestSubmitBodySchema = z.object({
    answers: checklistAnswersSchema,
    checklistVersion: z.number(),
    aircraftId: z.guid().nullable(),
    pilotInCommandId: z.guid().nullable(),
    validForHours: z.number().min(1).max(24),
    id: z.guid().optional(),
    filledAt: z.iso.datetime().optional(),
})

export const guestSubmitBodySchema = z.union([
    retroactiveGuestSubmitBodySchema,
    preemptiveGuestSubmitBodySchema,
])
export type GuestSubmitBody = z.infer<typeof guestSubmitBodySchema>

export const getChecklistSubmissionsQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(20),
    cursorFilledAt: z.string().optional(),
    cursorId: z.guid().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    userId: z.guid().optional(),
})
export type GETChecklistSubmissionsQuery = z.infer<typeof getChecklistSubmissionsQuerySchema>
